import repeat from 'lodash/repeat';
import endsWith from 'lodash/endsWith';

export const PARTIAL_VIN_SUFFIX = '1111111';
export const PARTIAL_VIN_OLD_SUBFIX = '111111';
export const PARTIAL_VIN_YEAR_CUTOFF = 1981;

/**
 * Note: As we started to digest car data directly from partners,
 * we are seeing vins with suffix 0s coming in.
 */
export const PARTIAL_VIN_SUFFIX_ZERO = '000000';

const ALL_ZERO_ENDING_REGEX = /0000000$/;

/**
 * Calculate the check digit of VIN
 * @param vin
 * @returns checkDigit, possible value: 0...9 and X
 */
export const vinCheckDigit = (vin: string) => {
  const formattedVin = vin.toUpperCase();

  const map = {
    A: 1,
    B: 2,
    C: 3,
    D: 4,
    E: 5,
    F: 6,
    G: 7,
    H: 8,
    J: 1,
    K: 2,
    L: 3,
    M: 4,
    N: 5,
    P: 7,
    R: 9,
    S: 2,
    T: 3,
    U: 4,
    V: 5,
    W: 6,
    X: 7,
    Y: 8,
    Z: 9,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    0: 0,
  };

  const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

  let products = 0;
  for (let i = 0; i < formattedVin.length; i++) {
    products += map[formattedVin[i]] * weights[i];
  }

  const checkDigit = products % 11;

  if (checkDigit === 10) return 'X';

  return `${checkDigit}`;
};

/**
 * @param possibleValues - string like: (4:C)(5:V)(6:8)(7:12)(11:ABCDE)(12:LR)
 * @returns possible11thValues
 */
export const getPossible11thValues = (possibleValues: string) => {
  const match = /11:(?<possible11thValue>[A-Z0-9]+)/.exec(possibleValues);
  const possible11thValues = (match?.groups?.['possible11thValue'] || '').split('');
  return possible11thValues;
};

/**
 * Returns a masked version of the VIN.
 * NOTE: supports both full and partial VIN as input.
 *
 * @param vin
 */
export const maskVIN = (vin: string): string => {
  return `${vin.slice(0, 9)}${repeat('X', vin.length - 9)}`;
};

/**
 * Replace trailing 0000000 to 1111111 because ezlynx only recognize 1111111 ending partial vin
 * @param vin
 */
export function replaceVinTrailing7ZerosForEzlynx(vin: string): string {
  if (vin && ALL_ZERO_ENDING_REGEX.test(vin)) {
    return vin.replace(ALL_ZERO_ENDING_REGEX, PARTIAL_VIN_SUFFIX);
  }
  return vin;
}

/**
 * Pending on 'isPartial' flag, return full vin or partial vin value.
 * @param {} vin
 * @param {*} isPartial
 * @param {*} year
 * @returns
 */
export const getVinOrPartialVin = (vin: string, isPartial: boolean, year?: string): string => {
  if (!isPartial) return vin;

  // partial vin is between 4 and 10 in length
  // If it's old car, older than 1981, remove all trailing 1s.
  if (year && parseInt(year, 10) < PARTIAL_VIN_YEAR_CUTOFF) return vin.replace(/(.{4,}?)1+$/, '$1');

  // Otherwise we can just get first 10 digits.
  return vin.slice(0, 10);
};

/**
 * Get proper partial vin based on year of the car.
 * @param {*} partialVin
 * @param {*} year
 * @returns
 */
export const getPartialVinWithSuffix = (partialVin: string, year?: string): string => {
  // If car's year is less than 1981, append six 1s. Otherwise append seven 1s.
  return year && parseInt(year, 10) < PARTIAL_VIN_YEAR_CUTOFF
    ? `${partialVin}${PARTIAL_VIN_OLD_SUBFIX}`
    : `${partialVin}${PARTIAL_VIN_SUFFIX}`;
};

/**
 * Check if vin has partial vin suffix: '000000', '111111', '1111111'. If it's, it is partial vin
 * @param vin
 * @returns
 */
export const vinHasPartialVinSuffix = (vin?: string): boolean => {
  return (
    !!vin &&
    (endsWith(vin, PARTIAL_VIN_SUFFIX_ZERO) ||
      endsWith(vin, PARTIAL_VIN_SUFFIX) ||
      endsWith(vin, PARTIAL_VIN_OLD_SUBFIX))
  );
};
